import React, { useEffect, useRef, useState } from 'react';

export interface UseConditionTooltipOptions {
  ref: React.MutableRefObject<HTMLElement | null>;
  ignoreHeight?: boolean;
}

/**
 * @description Display tooltip only when the referenced element has been truncated.
 */
export const useConditionalTooltip = (
  options?: Partial<UseConditionTooltipOptions>,
) => {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const defaultRef = useRef<HTMLElement | null>(null);
  const passedInRef = options?.ref;
  const ref = passedInRef ?? defaultRef;
  const ignoreHeight = options?.ignoreHeight ?? false;

  useEffect(() => {
    if (ref.current) {
      const scrollWidth = Math.ceil(ref.current.scrollWidth);
      const clientWidth = Math.floor(ref.current.clientWidth);
      const scrollHeight = Math.ceil(ref.current.scrollHeight);
      const clientHeight = Math.floor(ref.current.clientHeight);
      const widthOverflow = scrollWidth > clientWidth;
      const heightOverflow = scrollHeight > clientHeight;
      const overflow = ignoreHeight
        ? widthOverflow
        : widthOverflow || heightOverflow;
      setShouldShowTooltip(overflow);
    }
  }, [ignoreHeight, ref]);

  return {
    tooltipProps: {
      disableFocusListener: !shouldShowTooltip,
      disableHoverListener: !shouldShowTooltip,
      disableTouchListener: !shouldShowTooltip,
    },
    // The same answer as a plain boolean, for callers that have to drive the
    // tooltip's `open` themselves rather than let it manage its own listeners.
    // That is the only option when the element that takes focus is an ANCESTOR
    // of the truncated one — MUI's Tab button around a rail tab's label, say —
    // because `tooltipProps` can only ever wire listeners to the element the
    // tooltip wraps, and focus never reaches it (AXHE-4380).
    isTruncated: shouldShowTooltip,
  };
};
